// src/components/ClientEditModal.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import { useAuthContext } from '../context/AuthContext';

// --- FUNCIONES DE CONVERSIÓN ---
// Las definimos fuera del componente porque no dependen de ningún estado o prop.
const kgToLbs = (kg) => {
  if (!kg || isNaN(kg)) return '';
  return (kg * 2.20462).toFixed(1);
};
const lbsToKg = (lbs) => {
  if (!lbs || isNaN(lbs)) return '';
  return (lbs / 2.20462).toFixed(1);
};
const cmToFtIn = (cm) => {
  if (!cm || isNaN(cm)) return { ft: '', in: '' };
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = (totalInches % 12).toFixed(1);
  return { ft: feet, in: inches };
};
const ftInToCm = (ft, inches) => {
  const ftNum = parseFloat(ft) || 0;
  const inNum = parseFloat(inches) || 0;
  if (ftNum === 0 && inNum === 0) return '';
  const totalInches = ftNum * 12 + inNum;
  return (totalInches * 2.54).toFixed(1);
};

const ClientEditModal = ({ client, onClose, onClientUpdated }) => {
  // useAuthContext debe llamarse siempre
  let user = null;
  try {
    // Si el contexto está disponible, úsalo
    // (esto previene error si el contexto no está en el árbol, pero normalmente sí lo está)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    user = useAuthContext().user;
  } catch {
    user = null;
  }
    // --- Coach asignado ---
    const [coaches, setCoaches] = useState([]);
    const [assignedCoachId, setAssignedCoachId] = useState(client.assigned_coach_id || '');

    // Cargar lista de coaches si el usuario es coach o admin
    useEffect(() => {
      if (!user || (user.role !== 'coach' && user.role !== 'admin')) return;
      const fetchCoaches = async () => {
        try {
          const res = await api.get('/admin/users', { params: { role: 'coach' } });
          setCoaches(res.data || []);
        } catch {
          setCoaches([]);
        }
      };
      fetchCoaches();
    }, [user]);
  // --- Estados para datos no convertibles ---
  const [name, setName] = useState(client.name);
  const [email, setEmail] = useState(client.email);
  const [gender, setGender] = useState(client.profile?.gender || '');
  const [dob, setDob] = useState(client.profile?.date_of_birth || '');
  
  // --- Estado para el sistema de unidades ---
  const [unitSystem, setUnitSystem] = useState('imperial'); // Por defecto en Imperial

  // --- Estados para datos convertibles ---
  const [weightKg, setWeightKg] = useState(client.profile?.weight_kg || '');
  const [weightLbs, setWeightLbs] = useState(client.profile?.weight_lbs || '');
  const [heightCm, setHeightCm] = useState(client.profile?.height_cm || '');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');

  // --- Estados de UI ---
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Sincronizar altura en pies/pulgadas al cargar los datos del cliente
  useEffect(() => {
    if (client.profile?.height_cm) {
      const { ft, in: inches } = cmToFtIn(client.profile.height_cm);
      setHeightFt(ft);
      setHeightIn(inches);
    }
  }, [client]);

  // --- MANEJADORES DE CAMBIOS CON CONVERSIÓN ---
  const handleWeightKgChange = (e) => {
    const kg = e.target.value;
    setWeightKg(kg);
    setWeightLbs(kgToLbs(kg));
  };
  const handleWeightLbsChange = (e) => {
    const lbs = e.target.value;
    setWeightLbs(lbs);
    setWeightKg(lbsToKg(lbs));
  };
  const handleHeightCmChange = (e) => {
    const cm = e.target.value;
    setHeightCm(cm);
    const { ft, in: inches } = cmToFtIn(cm);
    setHeightFt(ft);
    setHeightIn(inches);
  };
  const handleHeightFtInChange = (e, part) => {
    let ft = heightFt;
    let inches = heightIn;
    if (part === 'ft') {
      ft = e.target.value;
      setHeightFt(ft);
    } else {
      inches = e.target.value;
      setHeightIn(inches);
    }
    setHeightCm(ftInToCm(ft, inches));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    const totalInches = (parseFloat(heightFt) || 0) * 12 + (parseFloat(heightIn) || 0);

    const updatedData = {
      name,
      email,
      profile: {
        gender: gender || null,
        date_of_birth: dob || null,
        weight_kg: parseFloat(weightKg) || null,
        weight_lbs: parseFloat(weightLbs) || null,
        height_cm: parseFloat(heightCm) || null,
        height_in: totalInches || null,
      }
    };

    try {
      // Actualizar datos generales
      const response = await axios.put(
        `http://127.0.0.1:8000/admin/clients/${client.id}`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Empezamos con los datos de la respuesta general
      let finalClientData = response.data;

      // Si el coach cambió y el usuario es coach o admin, actualizar coach asignado
      if (user?.role === 'coach' || user?.role === 'admin') {
        const coachIdToSend = assignedCoachId === '' ? null : parseInt(assignedCoachId, 10) || null;
        // Comparar contra el valor actual en la BD (devuelto por el endpoint general)
        const currentCoachId = finalClientData.assigned_coach_id != null ? finalClientData.assigned_coach_id : null;
        if (coachIdToSend !== currentCoachId) {
          const assignRes = await api.put(`/admin/clients/${client.id}/assign-coach`, { coach_id: coachIdToSend });
          // Usar la respuesta del assign-coach que tiene assigned_coach_id actualizado
          finalClientData = assignRes.data;
        }
      }

      onClientUpdated(finalClientData);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (typeof detail === 'string' ? detail : 'Error al actualizar el perfil.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4">
      <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-2xl">
        <h2 className="text-2xl font-bold text-white mb-6">Editar Cliente: {client.name}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Selector de coach solo para coach/admin */}
                        {(user?.role === 'coach' || user?.role === 'admin') && (
                          <div className="md:col-span-2">
                            <label htmlFor="edit-coach" className="block text-sm font-medium text-gray-300">Coach Asignado</label>
                            <select
                              id="edit-coach"
                              value={assignedCoachId}
                              onChange={e => setAssignedCoachId(e.target.value)}
                              className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"
                            >
                              <option value="">Sin coach</option>
                              {coaches.map(c => (
                                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                              ))}
                              {/* Opción de autoasignarse si es coach */}
                              {user?.role === 'coach' && (
                                <option value={user.id}>Autoasignarme ({user.name})</option>
                              )}
                            </select>
                          </div>
                        )}
            <div>
              <label htmlFor="edit-name" className="block text-sm font-medium text-gray-300">Nombre Completo</label>
              <input type="text" id="edit-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="edit-email" className="block text-sm font-medium text-gray-300">Correo Electrónico</label>
              <input type="email" id="edit-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
            <div>
              <label htmlFor="edit-gender" className="block text-sm font-medium text-gray-300">Género</label>
              <select id="edit-gender" value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red">
                <option value="">No especificado</option>
                <option value="Male">Masculino</option>
                <option value="Female">Femenino</option>
              </select>
            </div>
            <div>
              <label htmlFor="edit-dob" className="block text-sm font-medium text-gray-300">Fecha de Nacimiento</label>
              <input type="date" id="edit-dob" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
            </div>
          </div>

          <div className="flex justify-center space-x-2 bg-gray-900 p-1 rounded-lg my-6">
            <button type="button" onClick={() => setUnitSystem('metric')} className={`w-full py-2 rounded-md transition-colors ${unitSystem === 'metric' ? 'bg-marizan-red text-white' : 'text-gray-400 hover:bg-gray-700'}`}>Métrico (kg/cm)</button>
            <button type="button" onClick={() => setUnitSystem('imperial')} className={`w-full py-2 rounded-md transition-colors ${unitSystem === 'imperial' ? 'bg-marizan-red text-white' : 'text-gray-400 hover:bg-gray-700'}`}>Imperial (lbs/ft,in)</button>
          </div>

          {unitSystem === 'metric' ? (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="weight-kg" className="block text-sm font-medium text-gray-300">Peso (kg)</label>
                <input type="number" id="weight-kg" step="0.1" value={weightKg} onChange={handleWeightKgChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
              </div>
              <div>
                <label htmlFor="height-cm" className="block text-sm font-medium text-gray-300">Altura (cm)</label>
                <input type="number" id="height-cm" step="0.1" value={heightCm} onChange={handleHeightCmChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="weight-lbs" className="block text-sm font-medium text-gray-300">Peso (lbs)</label>
                <input type="number" id="weight-lbs" step="0.1" value={weightLbs} onChange={handleWeightLbsChange} className="mt-1 block w-full p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
              </div>
              <div>
                <label htmlFor="height-ft" className="block text-sm font-medium text-gray-300">Altura (ft, in)</label>
                <div className="flex space-x-2">
                  <input type="number" id="height-ft" value={heightFt} onChange={(e) => handleHeightFtInChange(e, 'ft')} placeholder="ft" className="mt-1 block w-1/2 p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
                  <input type="number" id="height-in" step="0.1" value={heightIn} onChange={(e) => handleHeightFtInChange(e, 'in')} placeholder="in" className="mt-1 block w-1/2 p-3 bg-gray-700 text-white font-bold rounded-md border-gray-600 focus:ring-marizan-red focus:border-marizan-red"/>
                </div>
              </div>
            </div>
          )}
          
          {error && <p className="text-red-500 text-sm text-center pt-4">{error}</p>}
          <div className="flex justify-end space-x-4 pt-6">
            <button type="button" onClick={onClose} className="bg-gray-600 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" disabled={loading} className="bg-marizan-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50">
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientEditModal;